import * as React from 'react';
import { useState } from 'react';
import styled from '@emotion/styled';
import { useFormik } from 'formik';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { BasicPageWidget } from '../../widgets/layout/BasicPageWidget';
import { InputContainerWidget } from '../../widgets/forms/InputContainerWidget';
import { TextInputType, TextInputWidget } from '../../widgets/forms/TextInputWidget';
import { ButtonType, ButtonWidget } from '../../widgets/controls/ButtonWidget';
import { useSystem } from '../../hooks/useSystem';
import { observer } from 'mobx-react';
import { AuthCodes } from '@foxbun/lib-goferal-types';
import { FooterWidget } from '../../widgets/layout/FooterWidget';
import { Notification, NotificationType } from '../../core/stores/NotificationStore';
import { DateTime, Duration } from 'luxon';
import { RouteError } from '@foxbun/lib-errors';
import { Routing } from '../Routing';
import { Font } from '../../fonts';

const logo = require('../../../media/logo.png');

export interface FormValues {
  profile: string;
  email: string;
  password: string;
  password_verify: string;
  birthday: string;
}

export const RegisterPage: React.FC = observer(() => {
  const system = useSystem();
  const navigate = useNavigate();
  const [serverErrors, setErrors] = useState<Partial<FormValues>>({});
  const [searchParams] = useSearchParams();
  const form = useFormik<FormValues>({
    initialValues: {
      profile: null,
      email: null,
      password: null,
      password_verify: null,
      birthday: null
    },
    validateOnChange: false,
    validate: (params) => {
      let errors = ['email', 'password', 'password_verify', 'profile', 'birthday'].reduce((prev, cur) => {
        if (!params[cur]) {
          prev[cur] = 'required';
        }
        return prev;
      }, {} as Partial<FormValues>);

      if (params.password && params.password.length < 8) {
        errors.password = 'password must be 8 chars or more';
      }
      if (params.password !== params.password_verify) {
        errors.password_verify = 'Passwords dont match';
      }

      let res = DateTime.fromSQL(params.birthday);
      if (!res.isValid) {
        errors.birthday = 'Invalid date';
      } else if (res.plus(Duration.fromObject({ years: 21 })) > DateTime.now()) {
        errors.birthday = 'Too young';
      }

      if (Object.keys(errors).length > 0) {
        return errors;
      }
    },
    onSubmit: async ({ profile, password, email, birthday }) => {
      //send to server
      try {
        await system.auth_client.register({
          profile,
          email,
          password,
          code: searchParams.get('code') || undefined,
          dob: DateTime.fromSQL(birthday).toJSDate()
        });
        system.notification_store.showNotification(
          new Notification({
            type: NotificationType.SUCCESS,
            desc: 'Registration was successful!'
          })
        );
        navigate(Routing.SIGN_IN);
      } catch (ex) {
        if (ex instanceof RouteError) {
          if (ex.error.code === AuthCodes.PROFILE_IN_USE) {
            setErrors({
              profile: 'Profile in use'
            });
          }
          if (ex.error.code === AuthCodes.INVALID_PROFILE) {
            setErrors({
              profile: 'Profile has invalid characters'
            });
          }
          if (ex.error.code === AuthCodes.USER_EXISTS) {
            setErrors({
              email: 'Email in use'
            });
          }
          if (ex.error.code === AuthCodes.REGISTRATION_DISABLED) {
            system.notification_store.showNotification(
              new Notification({
                type: NotificationType.ERROR,
                desc: 'Registration is temporarily disabled, please check our socials for more information!'
              })
            );
          }
        }
        console.log(ex);
      }
    }
  });

  if (system.user) {
    return <Navigate to={Routing.HOME} />;
  }

  return (
    <BasicPageWidget>
      <S.Container>
        <S.Logo src={logo} />
        <S.Phrase>Fill in the form to register below</S.Phrase>
        <InputContainerWidget label="Profile name" error={serverErrors.profile || form.errors.profile}>
          <TextInputWidget value={form.values.profile} onChange={form.getFieldHelpers('profile').setValue} />
        </InputContainerWidget>
        <InputContainerWidget label="Email" error={serverErrors.email || form.errors.email}>
          <TextInputWidget
            type={TextInputType.EMAIL}
            value={form.values.email}
            onChange={form.getFieldHelpers('email').setValue}
          />
        </InputContainerWidget>
        <InputContainerWidget label="Birthday" error={serverErrors.birthday || form.errors.birthday}>
          <TextInputWidget
            type={TextInputType.DATE}
            value={form.values.birthday}
            onChange={form.getFieldHelpers('birthday').setValue}
          />
        </InputContainerWidget>
        <InputContainerWidget label="Password" error={form.errors.password}>
          <TextInputWidget
            type={TextInputType.PASS}
            value={form.values.password}
            onChange={form.getFieldHelpers('password').setValue}
          />
        </InputContainerWidget>
        <InputContainerWidget label="Password Verify" error={form.errors.password_verify}>
          <TextInputWidget
            type={TextInputType.PASS}
            value={form.values.password_verify}
            onChange={form.getFieldHelpers('password_verify').setValue}
          />
        </InputContainerWidget>
        <S.Buttons>
          <ButtonWidget
            type={ButtonType.SECONDARY}
            label="Back"
            action={() => {
              navigate(Routing.SIGN_IN);
            }}
          />
          <ButtonWidget
            loading={form.isSubmitting}
            label="Register"
            action={() => {
              form.submitForm();
            }}
          />
        </S.Buttons>
      </S.Container>
      <FooterWidget />
    </BasicPageWidget>
  );
});
namespace S {
  export const Container = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    row-gap: 10px;
    justify-content: center;
    height: 100%;
  `;

  export const Buttons = styled.div`
    display: flex;
    flex-direction: column;
    row-gap: 10px;
    align-items: stretch;
    width: 250px;
  `;

  export const Phrase = styled.div`
    color: ${(p) => p.theme.text.secondary};
    font-family: ${Font.Special};
    text-align: center;
    margin-bottom: 30px;
  `;

  export const Logo = styled.img`
    width: 300px;
  `;
}
